import { useState, useCallback, useRef } from 'react'
import { useHistory } from "react-router-dom";

// Context
import { NavigatorContext } from './navigator-context'
import { useGlobalState } from '../global-state';

// Functions
import omit from 'lodash/omit';
import isExact from '../../functions/is-exact';

// Hydratable Functions
import { getRoute as getRouteFunction } from './functions/get-route'
import { getRoutePath as getRoutePathFunction } from './functions/get-route-path'
import { matchRoute as matchRouteFunction } from './functions/match-route';
import { generateRoutePath } from './functions/generate-route-path';

const DefaultPageLoader = <>Yükleniyor...</>

const exactRoutes = (route1, route2) => {
	return (isExact(
		{...omit((route1 ?? {}), 'component'), match: omit(route1?.match ?? {}, 'pattern')},
		{...omit((route2 ?? {}), 'component'), match: omit(route2?.match ?? {}, 'pattern')},
	));
}

export const NavigatorProvider = ({ routes, layouts, children, serverProps, pageLoader = <DefaultPageLoader /> }) => {
	const { setError, setNotFound } = useGlobalState();
	const history = useHistory();
	const routeHistory = useRef([serverProps?.initialRoute]);

	const [activeRoute, setActiveRoute] = useState(serverProps?.initialRoute);
	
	const goBack = (urlKey, params, redirectOpts) => {
		return new Promise((resolve, reject) => {
			if(routeHistory.current.length > 1) {
				history.goBack();
				resolve(routeHistory.current[1]);
			}
			else {
				if(urlKey) {
					redirect(urlKey, params, redirectOpts);
					resolve();
				}
				else {
					reject();
				}
			}
		})
	}

	const onChangeRoute = useCallback((route) => {
		if(!exactRoutes(activeRoute, route)) {
			if(route.key !== 'error') {
				setError(false);
			}
			if(route.key !== 'notFound') {
				setNotFound(false);
			}
			
			routeHistory.current = [route, routeHistory.current[0]];
			setActiveRoute(route);
		}
	}, [activeRoute]);

	const getRoute = (routeKey) => {
		return getRouteFunction({
			routeKey,
			routes
		});
	}

	const getRoutePath = (routeKey, params, { parseQueryParams, queryParamKey } = {}) => {
		return getRoutePathFunction({
			routes,
			routeKey,
			params,
			parseQueryParams,
			queryParamKey,
		});
	}

	const matchRoute = (url) => {
		return matchRouteFunction({
			url: url,
			routes: routes
		});
	}

	const redirect = useCallback((urlKey, params, redirectOpts = {}) => {
		const routePath = redirectOpts.raw ? urlKey : getRoutePath(urlKey, params);
		if(routePath) {
			if(redirectOpts.force || routePath !== window.location.pathname) {
				if(redirectOpts.target) {
					window.open(routePath, redirectOpts.target).focus();
				}
				else if(redirectOpts.replace) {
					history.replace(routePath);
				}
				else if(redirectOpts.hard) {
					window.location.href = routePath;
				}
				else {
					history.push(routePath);
				}
			}
		}
		else {
			console.error('Route not found:', urlKey)
		}
	}, [routes]);

	return (
		<NavigatorContext.Provider value={{
			routes: routes, 
			layouts: layouts,
			setActiveRoute,
			onChangeRoute,
			activeRoute,
			getRoute,
			matchRoute,
			redirect,
			getRoutePath,
			generateRoutePath,
			goBack,
			serverProps,
			PageLoader: pageLoader,
		}}>
			{children}
		</NavigatorContext.Provider>
	)
}